import type { PromotionView } from '@/api/generated';
import type { GrantedCard } from '@/api/rateCards';

/**
 * A promotion as the cost math reads it: only its kind and discount (`kind`, `percentBp`,
 * `amountMinor`). A saved `PromotionView` is one, and so is the New promotion page's draft
 * (TMMRSL-0073), so the preview needs no API call.
 */
export type PromotionDiscount = Partial<PromotionView>;

/**
 * Integer `value * multiplier / divisor` with half-up rounding (0.5 → next integer
 * away from zero). Never uses floating-point on the minor-unit product.
 */
function mulDivRoundHalfUp(value: number, multiplier: number, divisor: number): number {
  const product = value * multiplier;
  const half = Math.trunc(divisor / 2);
  if (product >= 0) return Math.trunc((product + half) / divisor);
  return -Math.trunc((-product + half) / divisor);
}

/** Retail and wholesale of the adopted card's monthly base, in that card's currency. */
export interface CostBasis {
  retail: number;
  wholesale: number;
  currency: string;
}

/**
 * The cost math's inputs from the adopted granted card, or `null` when there are none: its
 * `baseMinor` is the retail and its `baseWholesaleMinor` the wholesale (TMMAPI-0155), both in the
 * card's `currency`.
 */
export function costBasisOf(tenantCard: GrantedCard | null): CostBasis | null {
  const retail = tenantCard?.baseMinor;
  const wholesale = tenantCard?.baseWholesaleMinor;
  const currency = tenantCard?.currency;
  if (retail === undefined || wholesale === undefined || !currency) return null;
  return { retail, wholesale, currency };
}

export interface CostRows {
  retail: number;
  discount: number;
  wholesale: number;
  applicable: boolean;
}

/** Discount in minor units. Amount is capped at retail. Trial/offer → 0. */
export function discountMinor(promo: PromotionDiscount, retail: number): number {
  if (promo.kind === 'TRIAL_DAYS' || promo.kind === 'OFFER') return 0;
  if (promo.kind === 'AMOUNT_OFF_VARIABLE') {
    const amount = promo.amountMinor ?? 0;
    return amount > retail ? retail : amount;
  }
  return mulDivRoundHalfUp(retail, promo.percentBp ?? 0, 10000);
}

/**
 * Three cost-card rows on one subscriber / one statement: retail, discount, wholesale. No share is
 * computed per tenant — the reseller's revenue share is a reseller-wide figure (H §7.4,
 * TMMRSL-0029).
 */
export function costRows(promo: PromotionDiscount, retail: number, wholesale: number): CostRows {
  const applicable = promo.kind !== 'TRIAL_DAYS' && promo.kind !== 'OFFER';
  const discount = discountMinor(promo, retail);
  return { retail, discount, wholesale, applicable };
}
